import { type CourseSection, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { setCookie } from "cookies-next";
import {
	ChevronsDownUp,
	ChevronsUpDown,
	Circle,
	Volume2Icon,
} from "lucide-react";
import { memo, useMemo } from "react";
import { FaCheckCircle } from "react-icons/fa";

import type { CourseSectionHeaderProps } from "./CourseSectionHeader";
import { useCourseProgress } from "~/hooks";
import useFreeCourse from "~/hooks/useFreeCourse";
import useTrackCTA from "~/hooks/useTrackCTA";

interface CourseSectionHeaderControlsProps extends CourseSectionHeaderProps {}

const CourseSectionHeaderControls = ({
	section,
	course,
	setUserAction,
	isExpanded,
	isFreeSection,
	hasCourseAccess,
	isCompleted,
}: CourseSectionHeaderControlsProps) => {
	const { isSignedIn } = useAuth();

	const { isLoading, addCourseProgress, removeCourseProgress } =
		useCourseProgress(course);

	const subSectionWithAudio = useMemo(() => {
		return section.subSections?.find(
			(subSection) => subSection.audioTimestamp !== null,
		);
	}, [section.subSections]);

	const trackCTA = useTrackCTA();
	const { openClaimIfOffered } = useFreeCourse(course._id);

	return (
		<div className="-mr-1 phablet:-mr-2 flex">
			{(section.audioTimestamp !== null || subSectionWithAudio) && (
				<button
					onClick={(e) => {
						e.stopPropagation();

						const audioElement = document.getElementById(
							"course-audio-player-tag",
						) as HTMLAudioElement;

						audioElement.currentTime =
							section.audioTimestamp !== null
								? section.audioTimestamp
								: subSectionWithAudio?.audioTimestamp!;

						audioElement.play();
					}}
					className="course-section-audio-btn h-8 w-8 rounded-full p-1.5 hover:bg-slate-700/5 dark:hover:bg-slate-700/50"
				>
					<Volume2Icon size={20} />
				</button>
			)}

			{!isFreeSection && (
				<button
					disabled={isLoading}
					onClick={async (e) => {
						e.stopPropagation();
						if (hasCourseAccess) {
							if (!isCompleted) {
								addCourseProgress(section as CourseSection);
							} else {
								removeCourseProgress(section as CourseSection);
							}
						} else if (!isSignedIn) {
							setCookie("audited-course-id", course._id);

							trackCTA(UserAction.SIGN_UP, "Course Section Mark As Complete");

							setUserAction(UserAction.SIGN_UP);
						} else if (!hasCourseAccess) {
							if (openClaimIfOffered()) return;

							trackCTA(
								UserAction.BECOME_A_MEMBER,
								"Course Section Mark As Complete",
							);

							setUserAction(UserAction.BECOME_A_MEMBER);
						}
					}}
					className="course-section-progress-btn h-8 w-8 rounded-full p-1.5 hover:bg-slate-700/5 disabled:opacity-50 dark:hover:bg-slate-700/50"
				>
					{!isCompleted ? (
						<Circle size={20} />
					) : (
						<FaCheckCircle size={20} className="text-emerald-400" />
					)}
				</button>
			)}

			<button className="course-section-expand-btn hidden h-8 w-8 rounded-full p-1.5 hover:bg-slate-700/5 sm:block dark:hover:bg-slate-700/50">
				{!isExpanded ? (
					<ChevronsUpDown size={20} />
				) : (
					<ChevronsDownUp size={20} />
				)}
			</button>
		</div>
	);
};

export default memo(CourseSectionHeaderControls);
